import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Mic,
  MicOff,
  VideoOff,
  Phone,
  PhoneOff,
  Clock,
  User,
  Bot,
  Circle,
} from 'lucide-react';
import { ChatMessage, InterviewPhase, InterviewSection, INTERVIEW_SECTIONS } from '../types/interview';
import { checkInterviewStartConditions } from '../utils/interviewStartCheck';
import { RefactoredSpeechSynthesisService, DEFAULT_SPEECH_CONFIG } from '../services/speech';
import { aiInterviewService, CandidateData } from '../services/aiInterviewService';
import {
  ScreenshotCaptureService,
  ScreenshotState,
  CapturedScreenshot,
} from '../services/screenshotCaptureService';
import { ScreenshotUploadService } from '../services/screenshotUploadService';
import { interviewApi } from '../services/api';
import { isDevelopment } from '../config/environment';

// AI がレスポンスにこのマーカーを含めると、TTS 終了後にモーダルを表示する
const SECTION_COMPLETE_MARKER = '[[SECTION_COMPLETE]]';

interface AIInterviewSystemProps {
  companyId?: string;
  companyName?: string;
  interviewId?: string;
  candidateId: string;
  candidateName: string;
  position: string;
  industry: string;
  currentIndustry: string;
  currentPosition: string;
  isExperienced: boolean;
  candidateType?: 'chuuto' | 'shinsotsu';
  candidateToken?: string;
}

const AIInterviewSystem: React.FC<AIInterviewSystemProps> = ({
  companyId,
  companyName,
  interviewId,
  candidateId,
  candidateName,
  position,
  industry,
  currentIndustry,
  currentPosition,
  isExperienced,
  candidateType = 'chuuto',
  candidateToken,
}) => {
  const [isInterviewStarted, setIsInterviewStarted] = useState<boolean>(false);
  const [isSessionPrepared, setIsSessionPrepared] = useState<boolean>(false);
  const [showInstructionModal, setShowInstructionModal] = useState<boolean>(true);
  // showEndConfirmModal は削除（手動終了機能に統合）
  const [interviewSessionStatus, setInterviewSessionStatus] = useState<string | null>(null);
  const [showResumeModal, setShowResumeModal] = useState<boolean>(false);
  // 「最初からやり直す」の確認ステップ（誤タップで会話ログが消えるのを防ぐ）
  const [showRestartConfirm, setShowRestartConfirm] = useState<boolean>(false);
  const [resumeData, setResumeData] = useState<{
    currentPhase: InterviewSection;
    messages: Array<{ speaker: string; message: string }>;
    updatedAt: string;
  } | null>(null);
  const [isLoadingSessionStatus, setIsLoadingSessionStatus] = useState<boolean>(true);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const videoEnabled = true; // カメラは常にオン
  const [timeElapsed, setTimeElapsed] = useState<number>(0);
  // 面接開始前は 'waiting'。開始処理で 'active' に遷移する。
  // （初期値を 'active' にすると、開始ボタンを押す前から経過時間タイマーが回ってしまう）
  const [interviewPhase, setInterviewPhase] = useState<InterviewPhase>('waiting');
  const [speechRecognition, setSpeechRecognition] = useState<SpeechRecognition | null>(null);
  const [currentTranscript, setCurrentTranscript] = useState<string>('');
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string>('');
  // 面接中にカメラ/マイクの権限が失効した際、権限が戻るまで面接を一時停止するための状態
  const [permissionBlocked, setPermissionBlocked] = useState<'camera' | 'microphone' | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [waveformHeights, setWaveformHeights] = useState<number[]>([12, 16, 20, 14, 18]);

  // AI思考中の状態管理
  const [isAIThinking, setIsAIThinking] = useState<boolean>(true);
  const [thinkingStage, setThinkingStage] = useState<
    'listening' | 'understanding' | 'preparing' | 'ready'
  >('listening');
  const [thinkingProgress, setThinkingProgress] = useState<number>(0);

  // AI読み上げ中テキスト表示の状態管理
  const [currentSpeechText, setCurrentSpeechText] = useState<string>('');
  // 直前の面接官（AI）テロップを持続表示するための状態
  const [lastInterviewerText, setLastInterviewerText] = useState<string>('');

  // 音声速度の状態管理（slow: 1.0, normal: 1.25, fast: 1.5）
  const [speechSpeed, setSpeechSpeed] = useState<'slow' | 'normal' | 'fast'>('normal');

  // 面接セッションのステータスを取得する関数
  // 戻り値の status は、呼び出し元がAIセッションの新規作成可否を判断するために使う。
  const fetchInterviewSessionStatus = useCallback(async (): Promise<string> => {
    if (!interviewId) {
      setIsLoadingSessionStatus(false);
      return 'not_started';
    }

    if (!candidateToken) {
      console.warn('🚫 候補者トークンがないため、面接ステータス取得をスキップします', { interviewId });
      setIsLoadingSessionStatus(false);
      return 'not_started';
    }

    try {

      const response = await interviewApi.getInterviewStatus(interviewId, candidateToken);

      if (!response.success) {
        console.error('🚫 APIエラーレスポンス:', {
          message: response.message,
          error: response.error,
        });
        throw new Error(`API error: ${response.message}`);
      }


      setInterviewSessionStatus(response.data.status);

      // completedまたはevaluatedまたはcancelledの場合、指示モーダルを非表示にする
      if (
        response.data.status === 'completed' ||
        response.data.status === 'evaluated' ||
        response.data.status === 'cancelled'
      ) {
        setShowInstructionModal(false);
      }

      // in_progress の場合は再開データを取得して再開モーダルを表示
      if (response.data.status === 'in_progress') {
        try {
          const resumeRes = await interviewApi.getResumeData(interviewId, candidateToken);
          if (resumeRes.success && resumeRes.data && resumeRes.data.messages?.length > 0) {
            setResumeData({
              currentPhase: resumeRes.data.currentPhase as InterviewSection,
              messages: resumeRes.data.messages,
              updatedAt: resumeRes.data.updatedAt,
            });
            setShowResumeModal(true);
            setShowInstructionModal(false);
          }
        } catch (e) {
          console.warn('再開データ取得失敗:', e);
        }
      }

      return response.data.status;
    } catch (error) {
      console.error('⚠️ 面接セッションステータス取得エラー:', error);
      // エラーの場合は通常の面接フローを継続
      setInterviewSessionStatus('not_started');
      return 'not_started';
    } finally {
      setIsLoadingSessionStatus(false);
    }
  }, [interviewId, candidateToken]);

  // セクション（パート）管理
  const [currentSection, setCurrentSection] = useState<InterviewSection>('INTRO');
  const [isPhaseTransitioning, setIsPhaseTransitioning] = useState<boolean>(false);
  const [showAdvanceConfirm, setShowAdvanceConfirm] = useState<boolean>(false);
  // AI が SECTION_COMPLETE_MARKER を返した場合、TTS 終了後にモーダルを出すためのフラグ
  const pendingSectionCompleteRef = useRef<boolean>(false);
  // 音声合成初期化 useEffect ([] 依存)のクロージャから最新値を参照するための ref
  const currentSectionRef = useRef<InterviewSection>('INTRO');
  useEffect(() => {
    currentSectionRef.current = currentSection;
  }, [currentSection]);
  // 同じく [] 依存クロージャから呼ぶための ref。直接 completeInterview を呼ぶと
  // マウント時のクロージャに閉じた chatHistory=[] が使われ、完了API送信が
  // 「会話ログなし」でスキップされてしまう（自動終了時に completed にならないバグ）。
  const completeInterviewRef = useRef<(reason?: string) => void>(() => {});

  // デバイス・接続チェック
  type CheckStatus = 'checking' | 'ok' | 'error' | 'unknown';
  const [cameraCheckStatus, setCameraCheckStatus] = useState<CheckStatus>('checking');
  const [micCheckStatus, setMicCheckStatus] = useState<CheckStatus>('checking');

  // ユーザーターン管理
  const [isUserTurn, setIsUserTurn] = useState<boolean>(false);

  // 面接終了通知管理
  const [showEndingNotification, setShowEndingNotification] = useState<boolean>(false);
  const [endingReason, setEndingReason] = useState<string>('');

  // 面接キャンセル関連の状態管理
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState<string>('');

  // 面接手動終了関連の状態管理
  const [showManualEndConfirmModal, setShowManualEndConfirmModal] = useState<boolean>(false);

  // AI面接セッション管理
  const [aiSessionId, setAiSessionId] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string>('');

  // 初期メッセージを保存する状態
  const [preparedInitialMessage, setPreparedInitialMessage] = useState<string>('');

  // スクリーンショット機能の状態管理
  const [screenshotState, setScreenshotState] = useState<ScreenshotState>({
    isCapturing: false,
    screenshotCount: 0,
    totalSize: 0,
  });
  const [isScreenshotEnabled] = useState<boolean>(true);
  const [screenshotError, setScreenshotError] = useState<string>('');
  const screenshotCaptureRef = useRef<ScreenshotCaptureService | null>(null);
  const screenshotUploadRef = useRef<ScreenshotUploadService | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [capturedScreenshots, setCapturedScreenshots] = useState<CapturedScreenshot[]>([]);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const waveformIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const speechSynthesisRef = useRef<RefactoredSpeechSynthesisService | null>(null);
  const speechRecognitionRef = useRef<SpeechRecognition | null>(null);
  const speechRestartTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // エラーメッセージを統合し、ユーザーアクションを促すヘルパー関数
  const normalizeAIError = (error: string): string => {
    if (
      error.includes('timeout') ||
      error.includes('接続に失敗') ||
      error.includes('接続できません') ||
      error.includes('ECONNABORTED') ||
      error.includes('ETIMEDOUT')
    ) {
      return 'AI面接システムに接続できません';
    }
    return error;
  };

  // AI面接セッションの事前準備（ページロード時に実行）
  const prepareAISession = useCallback(async (): Promise<void> => {
    setAiError('');

    try {
      // AI面接システム接続テスト
      const connectionTest = await aiInterviewService.testConnection();

      if (!connectionTest.success) {
        console.error('❌ AI面接システム接続テスト失敗:', connectionTest.error);
        const normalizedError = normalizeAIError(
          connectionTest.error || 'AI面接システムに接続できません'
        );
        setAiError(normalizedError);
        return;
      }


      // AIセッションを事前に作成
      const sessionId = await createAISession();

      if (!sessionId) {
        console.error('❌ AI面接セッション事前作成に失敗');
        return;
      }


      // AIセッションを開始して初期メッセージを事前取得
      const startResult = await aiInterviewService.startSession(sessionId);

      if (!startResult.success || !startResult.data?.initialMessage) {
        throw new Error(startResult.error || 'セッション開始に失敗しました');
      }

      const initialMessage = startResult.data.initialMessage;
      setPreparedInitialMessage(initialMessage);

      setIsSessionPrepared(true);
    } catch (error) {
      console.error('❌ AI面接セッション事前準備エラー:', error);
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      const normalizedError = normalizeAIError(`AI面接の準備に失敗しました: ${errorMessage}`);
      setAiError(normalizedError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // AI面接セッションを作成
  const createAISession = async (): Promise<string | null> => {
    try {
      setAiError('');

      // 候補者データを準備（プロップスから取得）
      // 面接開始時は経歴サマリーは空（面接中に聞き出すため）
      const candidateData: CandidateData = {
        company_id: companyId || '', // AI面接官プロンプト取得用
        company_name: companyName || '',
        industry: industry,
        position: position,
        current_industry: currentIndustry,
        current_position: currentPosition,
        is_experience: isExperienced,
        candidate_type: candidateType,
        closing_comment: '',
        candidate_name: candidateName,
        interview_duration: 60,
        careerSummary: undefined, // 面接開始時は空
        backend_session_id: interviewId, // 発行(schedule)済みのセッションIDを使い、開始ごとの新規作成を防ぐ
      };


      const result = await aiInterviewService.createSession(candidateData);


      if (result.success && result.data?.sessionId) {
        const sessionId = result.data.sessionId;
        setAiSessionId(sessionId);
        return sessionId;
      } else {
        console.error('❌ AIセッション作成失敗:', {
          error: result.error,
          data: result.data,
        });
        setAiError(result.error || 'セッション作成に失敗しました');
        return null;
      }
    } catch (error) {
      console.error('❌ createAISession エラーキャッチ:', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
      });
      const errorMessage =
        error instanceof Error ? error.message : 'AI面接システムに接続できません';
      const normalizedError = normalizeAIError(errorMessage);
      setAiError(normalizedError);
      return null;
    }
  };

  // 面接終了通知を表示
  const showInterviewEndingNotification = (reason: string) => {
    setEndingReason(reason);
    setShowEndingNotification(true);

    // 3秒間表示してから処理画面に移行
    setTimeout(() => {
      setShowEndingNotification(false);
      actuallyCompleteInterview();
    }, 3000);
  };

  // 面接キャンセル通知を表示
  const showInterviewCancelNotification = (reason: string) => {
    setEndingReason(reason);
    setShowEndingNotification(true);

    // 3秒間表示してからキャンセル処理画面に移行
    setTimeout(() => {
      setShowEndingNotification(false);
      actuallyCancelInterview();
    }, 3000);
  };

  // AI面接エージェントからの応答を取得
  const getAIResponse = async (userMessage: string, sessionId?: string): Promise<string> => {

    const currentSessionId = sessionId || aiSessionId;

    if (!currentSessionId) {
      console.error('❌ AIセッションIDが存在しません', {
        sessionId,
        aiSessionId,
        userMessage: userMessage.substring(0, 50) + '...',
      });
      throw new Error('AIセッションが作成されていません');
    }


    try {
      const result = await aiInterviewService.sendMessage(currentSessionId, userMessage);


      if (result.success && result.data?.agentMessage) {
        return result.data.agentMessage;
      } else {
        console.error('❌ AI応答取得失敗', {
          success: result.success,
          error: result.error,
          data: result.data,
          hasAgentMessage: !!result.data?.agentMessage,
        });
        throw new Error(result.error || 'AI面接官との通信に失敗しました');
      }
    } catch (error) {
      console.error('❌ getAIResponse エラーキャッチ', {
        error,
        errorMessage: error instanceof Error ? error.message : '不明なエラー',
        errorStack: error instanceof Error ? error.stack : undefined,
        sessionId: currentSessionId,
        userMessage: userMessage.substring(0, 50) + '...',
      });
      throw error;
    }
  };

  // 音声合成サービスの初期化
  useEffect(() => {
    const speechService = new RefactoredSpeechSynthesisService(
      DEFAULT_SPEECH_CONFIG,
      (isSpeaking) => {
        setIsSpeaking(isSpeaking);

        // 読み上げ終了時の追加処理
        if (!isSpeaking) {
          setCurrentSpeechText('');
          setCurrentTranscript('');
          setIsUserTurn(true);
          setIsAIThinking(false);
          // セクション完了マーカーがあればモーダルを表示
          if (pendingSectionCompleteRef.current) {
            pendingSectionCompleteRef.current = false;
            if (currentSectionRef.current === 'CLOSING') {
              // CLOSINGは最終パートのため「次のパートへ」モーダルの遷移先が存在せず、
              // showAdvanceConfirmをtrueにしても何も表示されないまま面接が
              // 止まってしまっていた（音声認識もCLOSINGでは再開しないため無反応に見える）。
              // CLOSING完了時は自動的に面接終了処理を呼ぶ。
              completeInterviewRef.current('面接が完了しました');
            } else {
              setShowAdvanceConfirm(true);
            }
          } else if (currentSectionRef.current !== 'CLOSING') {
            // 読み上げ終了直後にユーザーが話し始めても取りこぼさないよう、
            // 後段 useEffect の setTimeout(400ms) 再開を待たずに音声認識を即再開する。
            // （自然終了時に最初の発話が recognition 起動前に欠落し「音声が認識されません
            //   でした」となる問題への対策。既に起動中なら InvalidStateError は無視）
            // CLOSING パートは候補者の発話を受け付けないため、ここでの即時再開は行わない。
            const recognition = speechRecognitionRef.current;
            if (recognition) {
              try {
                recognition.start();
                setIsRecording(true);
              } catch (error: any) {
                if (error?.name === 'InvalidStateError') {
                  setIsRecording(true);
                } else {
                  console.warn('🎤 読み上げ終了時の音声認識即再開に失敗', error);
                }
              }
            }
          }
        }
      },
      (event) => {
        if (event.type === 'speaking_ended') {
          // 読み上げ終了時にテキストをクリア
          setCurrentSpeechText('');
          // 前ターンの認識結果が残らないよう、ユーザーターン開始時にクリア
          setCurrentTranscript('');

          // AI読み上げ終了時にユーザーターンをtrueに設定
          setIsUserTurn(true);
          setIsAIThinking(false); // AI思考状態をクリア
        }
      }
    );
    speechSynthesisRef.current = speechService;

    // スクリーンショットアップロードサービスの初期化
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL?.endsWith('/api/v1')
      ? process.env.REACT_APP_API_BASE_URL
      : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/api/v1`;

    screenshotUploadRef.current = new ScreenshotUploadService({
      apiBaseUrl: API_BASE_URL,
      maxBatchSize: 10, // バッチサイズ10ファイル
      timeoutMs: 60000, // 60秒タイムアウト
    });


    return () => {
      speechService.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // E2E テスト用フック（開発環境のみ）
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      (window as any).__e2e_submitResponse = (text: string) => submitResponse(text);
      (window as any).__e2e_showAdvanceModal = () => setShowAdvanceConfirm(true);
      (window as any).__e2e_getCurrentSection = () => currentSection;
    }
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // AI音声読み上げ機能
  const speakText = (text: string): void => {

    // AI音声開始前に音声認識を停止
    const recognition = speechRecognitionRef.current;
    if (recognition && isRecording) {
      recognition.stop();
      setIsRecording(false);
    }

    // 読み上げ中のテキストを設定
    setCurrentSpeechText(text);
    // 直前の面接官テキストを更新（読み上げ終了後も表示を維持）
    setLastInterviewerText(text);

    // 話している状態を手動でtrueに設定
    setIsSpeaking(true);

    if (speechSynthesisRef.current) {
      speechSynthesisRef.current.speak(text);
    } else {
      console.error('❌ 音声合成サービスが初期化されていません');
      // サービスがない場合も状態をリセット
      setTimeout(() => {
        setIsSpeaking(false);
      }, 3000);
    }
  };

  // 音声速度を変更する関数
  const handleSpeedChange = (speed: 'slow' | 'normal' | 'fast') => {
    const rates = { slow: 1.0, normal: 1.25, fast: 1.5 };
    setSpeechSpeed(speed);
    if (speechSynthesisRef.current) {
      speechSynthesisRef.current.updateConfig({ speakingRate: rates[speed] });
    }
  };

  // コンポーネントの初期化
  useEffect(() => {
    // 面接セッションのステータスを取得したうえで、まだ一度も開始していない
    // (not_started) 場合に限りAIセッションを事前準備する（モーダル表示中）。
    // ステータス確認を待たずに無条件で呼ぶと、完了済み(completed/evaluated)や
    // キャンセル済みの面接ページに再アクセスしただけでAIエージェント側に新規
    // セッションが作られ、その最初のメッセージ保存が既存の会話ログ全体を
    // 上書きしてしまう。in_progress の場合は別途「再開」フローに委ねる。
    fetchInterviewSessionStatus().then((status) => {
      // in_progress（再開）でも事前準備を行う。これが無いと isSessionPrepared が
      // 永遠に false のままになり、再開モーダルの「続きから再開する」も
      // 「最初からやり直す」→「面接を始める」も押せなくなる。
      // completed/evaluated/cancelled を除外する保護（会話ログ上書き防止）は維持。
      // in_progress の会話ログは /messages 側の「長い方を保持」ガードで守られる。
      if (status === 'not_started' || status === 'in_progress') {
        prepareAISession();
      }
    });
  }, [fetchInterviewSessionStatus, prepareAISession]);

  // デバイス・接続チェック（モーダル表示時に実行）
  useEffect(() => {
    if (!showInstructionModal || isInterviewStarted) return;

    const checkPermission = async (name: PermissionName): Promise<CheckStatus> => {
      try {
        const result = await navigator.permissions.query({ name });
        if (result.state === 'granted') return 'ok';
        if (result.state === 'denied') return 'error';
        return 'unknown'; // 'prompt' = まだ許可していない
      } catch {
        return 'unknown';
      }
    };

    checkPermission('camera' as PermissionName).then(setCameraCheckStatus);
    checkPermission('microphone' as PermissionName).then(setMicCheckStatus);
  }, [showInstructionModal, isInterviewStarted]);

  // 音声認識の初期化
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)
    ) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'ja-JP';

      recognition.onstart = () => {
        console.log('🎤 音声認識を開始しました');
        setIsRecording(true);
      };

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimTranscript = '';

        // event.results 全体から毎回再構築し、前ターン文字列との連結を防ぐ
        for (let i = 0; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        const normalizedFinal = finalTranscript.trim();
        const normalizedInterim = interimTranscript.trim();

        // 確定分・仮分を組み合わせて現在の内容を丸ごと上書き
        if (normalizedFinal || normalizedInterim) {
          setCurrentTranscript(
            `${normalizedFinal}${normalizedInterim ? ` [認識中...] ${normalizedInterim}` : ''}`.trim()
          );
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        // 認識が失敗しても今までログが一切出ず、「音声が認識できませんでした」
        // という定型応答だけが返るため原因が追えなかった。
        console.warn('🎤 音声認識エラー:', event.error);
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          setPermissionBlocked('microphone');
        }
        if (event.error !== 'no-speech') {
          setIsRecording(false);
        }
      };

      recognition.onend = () => {
        console.log('🎤 音声認識が終了しました');
        setIsRecording(false);
      };

      setSpeechRecognition(recognition);
      speechRecognitionRef.current = recognition;
    }
  }, []);

  // タイマー機能（自動終了なし）
  // カメラ/マイクの権限が失効している間は、記録される面接時間がずれないよう停止する
  useEffect(() => {
    if (interviewPhase === 'active' && !permissionBlocked) {
      intervalRef.current = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewPhase, permissionBlocked]); // completeInterviewは安定した関数なので依存配列から除外

  // スクリーンショット取得開始（リトライ機能付き）
  const startScreenshotCapture = async (retryCount = 0): Promise<void> => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 2000; // 2秒待機

    try {
      setScreenshotError('');

      if (!screenshotCaptureRef.current) {
        screenshotCaptureRef.current = new ScreenshotCaptureService((state: ScreenshotState) => {
          setScreenshotState(state);
        });
      }

      if (!videoRef.current) {
        throw new Error('Video要素が存在しません');
      }


      // カメラが起動していない場合は先に起動
      if (!videoStream && videoEnabled) {
        await startCamera();

        // カメラ起動後、少し待つ
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      // スクリーンショット取得開始（30秒間隔、カメラのみ）
      await screenshotCaptureRef.current.startCapture(
        {
          camera: true,
          interval: 30000, // 30秒間隔
          quality: 0.8, // JPEG品質80%
          maxWidth: 640,
          maxHeight: 480,
        },
        videoRef.current
      );

      // スクリーンショット状態をサーバーに通知
      if (interviewId) {
        try {
          // URL構築ロジック - 重複パス回避
          let API_BASE_URL: string;
          const ENV_API_URL = process.env.REACT_APP_API_BASE_URL;

          if (ENV_API_URL) {
            // 本番環境: URLが既に/api/v1で終わっているかチェック
            API_BASE_URL = ENV_API_URL.endsWith('/api/v1') ? ENV_API_URL : `${ENV_API_URL}/api/v1`;
          } else {
            // ローカル環境
            API_BASE_URL = 'http://localhost:3001/api/v1';
          }


          await fetch(`${API_BASE_URL}/screenshots/status`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              interviewId,
              status: 'started',
              timestamp: new Date().toISOString(),
            }),
          });
        } catch (error) {
          console.error('スクリーンショット状態通知エラー:', error);
        }
      }
    } catch (error) {
      console.error('❌ スクリーンショット取得開始エラー:', error);

      // リトライ可能なエラーかチェック
      const isRetryable =
        error instanceof Error &&
        (error.message.includes('カメラが他のアプリケーション') ||
          error.message.includes('初期化に失敗') ||
          error.message.includes('一時的'));

      if (isRetryable && retryCount < MAX_RETRIES) {
        setScreenshotError(
          `カメラの初期化に失敗しました。${RETRY_DELAY / 1000}秒後に再試行します... (${retryCount + 1}/${MAX_RETRIES})`
        );

        setTimeout(() => {
          startScreenshotCapture(retryCount + 1);
        }, RETRY_DELAY);
      } else {
        const errorMessage = error instanceof Error ? error.message : '不明なエラー';
        setScreenshotError(`スクリーンショット取得の開始に失敗しました: ${errorMessage}`);

        // 最終的なエラーの場合はユーザーガイドも表示
        if (error instanceof Error && error.message.includes('カメラの使用が許可されていません')) {
          setTimeout(() => {
            setScreenshotError(
              (prev) =>
                prev +
                '\n\n💡 対処方法:\n1. ブラウザのアドレスバー左側のアイコンをクリック\n2. カメラの権限を「許可」に変更\n3. ページを再読み込み'
            );
          }, 3000);
        }
      }
    }
  };

  // スクリーンショット取得停止・アップロード統合処理
  const stopScreenshotCapture = async (): Promise<void> => {
    try {

      if (!screenshotCaptureRef.current) {
        return;
      }

      // スクリーンショット取得停止
      const screenshots = await screenshotCaptureRef.current.stopCapture();
      setCapturedScreenshots(screenshots);


      // スクリーンショットデータが存在し、面接IDがある場合のみアップロード
      if (screenshots.length > 0 && interviewId && screenshotUploadRef.current) {

        const uploadResult = await screenshotUploadRef.current.uploadScreenshots(screenshots, {
          interviewId,
          candidateId,
          candidateName,
          companyId: companyId || '',
        });


        if (uploadResult.success) {
          if (uploadResult.summary.failed > 0) {
            console.warn(
              `⚠️ 部分的にアップロード成功: ${uploadResult.summary.successful}/${uploadResult.summary.total}個`
            );
            console.warn('失敗したファイル:', uploadResult.failedFiles);

            // 部分的失敗の場合でもエラーメッセージを表示
            setScreenshotError(
              `部分的なアップロード失敗: ${uploadResult.summary.failed}個のファイルがアップロードに失敗しました`
            );
          } else {
          }
        } else {
          console.error('❌ スクリーンショットアップロード失敗:', uploadResult);
          const errorMessages = uploadResult.failedFiles.map((f) => f.error).join(', ');
          setScreenshotError(`スクリーンショットアップロード失敗: ${errorMessages}`);
          // スクリーンショットは面接完了をブロックすべきでない非重要な背景処理のため、
          // ここで throw して面接完了フロー（呼び出し元のcatch）に伝播させない。
          // 候補者には「面接完了処理中にエラーが発生しました」という不要な不安を与えていた。
        }

        // スクリーンショット状態をサーバーに通知
        try {
          const API_BASE_URL = screenshotUploadRef.current.getApiBaseUrl();
          await fetch(`${API_BASE_URL}/screenshots/status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              interviewId,
              status: 'uploaded',
              timestamp: new Date().toISOString(),
            }),
          });
        } catch (statusError) {
          console.warn('スクリーンショット状態通知エラー:', statusError);
        }
      } else {
        console.warn('⚠️ スクリーンショットデータまたは面接IDが不正:', {
          screenshotCount: screenshots.length,
          hasInterviewId: !!interviewId,
          hasAiSessionId: !!aiSessionId,
        });
      }
    } catch (error) {
      console.error('❌ スクリーンショット停止・アップロードエラー:', error);
      setScreenshotError(
        'スクリーンショットの停止・アップロードに失敗しました: ' +
          (error instanceof Error ? error.message : '不明なエラー')
      );
      // スクリーンショットは面接完了をブロックすべきでない非重要な背景処理のため、
      // 呼び出し元（面接完了フロー）には伝播させない。
    }
  };

  // 面接開始
  const startInterview = async (): Promise<void> => {
    setIsInterviewStarted(true);
    setInterviewPhase('active');
    setShowInstructionModal(false);

    // エラーがある場合は面接を開始しない
    if (aiError) {
      console.error('❌ AI接続エラーがあるため面接を開始できません:', aiError);
      setInterviewPhase('waiting');
      setIsInterviewStarted(false);
      return;
    }

    // AIセッションIDが既に準備されているか確認
    if (!aiSessionId) {
      console.error('❌ AIセッションが準備されていません');
      setAiError('AI面接セッションが準備されていません。ページを再読み込みしてください。');
      setInterviewPhase('waiting');
      setIsInterviewStarted(false);
      return;
    }


    // バックエンドの面接ステータスを in_progress にする。
    // これを呼ばないと DB 上は not_started のままになり、ブラウザ更新等で
    // 中断した際に再開判定（status === 'in_progress'）が働かず、
    // 会話ログが保存されていても再開モーダルが出せない。
    // 失敗しても面接進行はブロックしない（ベストエフォート）。
    if (interviewId) {
      try {
        const startResult = await interviewApi.startInterview(interviewId, candidateToken);
        if (!startResult.success) {
          console.warn('⚠️ 面接ステータスの開始更新に失敗（面接は継続）:', startResult.message);
        }
      } catch (error) {
        console.warn('⚠️ 面接ステータスの開始更新でエラー（面接は継続）:', error);
      }
    }

    if (isScreenshotEnabled) {
      await startScreenshotCapture();
    }

    // 事前準備済みの初期メッセージをすぐに表示・発話
    try {

      if (!preparedInitialMessage) {
        throw new Error('初期メッセージが準備されていません');
      }

      // チャット履歴に初期メッセージを追加
      setChatHistory([
        {
          sender: 'ai',
          message: preparedInitialMessage,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);

      // AI挨拶をすぐに読み上げ
      speakText(preparedInitialMessage);

    } catch (error) {
      console.error('❌ 初期メッセージ表示エラー:', error);

      // エラーメッセージを表示
      const errorMessage: ChatMessage = {
        sender: 'system',
        message: `面接開始に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
        timestamp: new Date().toLocaleTimeString(),
      };

      setChatHistory([errorMessage]);

      // エラーが発生した場合は面接を停止
      setInterviewPhase('waiting');
      setIsInterviewStarted(false);
      if (screenshotState.isCapturing) {
        await stopScreenshotCapture();
      }
    }
  };

  // 面接完了処理（通知→実際の処理）
  const completeInterview = (reason: string = '面接が完了しました') => {
    showInterviewEndingNotification(reason);
  };
  // 毎レンダー更新し、[] 依存クロージャ（TTS終了コールバック）からでも
  // 最新の chatHistory / interviewId を閉じた completeInterview が呼ばれるようにする
  useEffect(() => {
    completeInterviewRef.current = completeInterview;
  });

  // 面接キャンセル処理（評価なし）
  const cancelInterview = (reason: string = '面接がキャンセルされました') => {
    showInterviewCancelNotification(reason);
  };

  // 面接キャンセル処理（録画保存・DB保存込み、評価なし）
  const actuallyCancelInterview = async (): Promise<void> => {
    setShowCancelConfirmModal(false);
    setInterviewPhase('processing'); // 処理画面に移行

    try {
      // スクリーンショット停止・保存
      if (screenshotState.isCapturing) {
        await stopScreenshotCapture();

        // 録画データが完全に処理されるまで少し待機
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      // 会話ログを準備
      const conversationLog = chatHistory.map((chat) => ({
        speaker: chat.sender === 'ai' ? 'interviewer' : 'candidate',
        message: chat.message,
        timestamp: chat.timestamp,
      }));

      // バックエンドに面接キャンセルを送信（interviewIdが存在する場合）
      if (interviewId) {
        try {

          const requestData = {
            reason: cancelReason || 'ユーザーが面接を途中でキャンセルしました',
            conversationLog: conversationLog,
            endTime: new Date().toISOString(),
          };


          const result = await interviewApi.cancelInterview(interviewId, requestData, candidateToken);


          // キャンセル後にセッションステータスを再取得して確認
          setTimeout(async () => {
            try {
              await interviewApi.getInterviewStatus(interviewId, candidateToken);
            } catch (statusError) {
              console.error('❌ ステータス確認エラー:', statusError);
            }
          }, 1000);

          if (result.success) {

            // 成功メッセージをチャット履歴に追加
            setChatHistory((prev) => [
              ...prev,
              {
                sender: 'system',
                message: '面接がキャンセルされました。会話ログは保存されました。',
                timestamp: new Date().toLocaleTimeString(),
              },
            ]);
          } else {
            console.error('❌ 面接キャンセルのDB保存失敗:', {
              resultSuccess: result.success,
              resultMessage: result.message,
              resultError: result.error,
              fullResult: result,
            });
            throw new Error(
              `面接キャンセル保存失敗: ${result.message || result.error || 'レスポンス解析エラー'}`
            );
          }
        } catch (error) {
          console.error('❌ 面接キャンセル保存エラー:', error);

          // エラーメッセージをチャット履歴に追加
          setChatHistory((prev) => [
            ...prev,
            {
              sender: 'system',
              message: `面接キャンセルの保存中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
              timestamp: new Date().toLocaleTimeString(),
            },
          ]);
        }
      }
    } catch (error) {
      console.error('❌ 面接キャンセル処理エラー:', error);

      // エラーメッセージをチャット履歴に追加
      setChatHistory((prev) => [
        ...prev,
        {
          sender: 'system',
          message: `面接キャンセル処理中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } finally {
      // 最終処理
      setInterviewPhase('cancelled'); // キャンセル状態に設定
      setIsInterviewStarted(false);
      setIsSessionPrepared(false);

      // キャンセル理由をリセット
      setCancelReason('');

      stopCamera();
      stopSpeaking();
      if (isRecording && speechRecognition) {
        speechRecognition.stop();
        setIsRecording(false);
      }

    }
  };

  // 面接完了処理（録画保存・DB保存込み）
  const actuallyCompleteInterview = async (): Promise<void> => {
    setInterviewPhase('processing'); // 処理画面に移行

    try {
      if (screenshotState.isCapturing) {
        await stopScreenshotCapture();

        // 録画データが完全に処理されるまで少し待機
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      // AI面接エージェントから面接データを取得（セッションが存在する場合）
      if (aiSessionId) {
        try {
          await aiInterviewService.getSession(aiSessionId);
        } catch (error) {
          console.error('AI面接データ取得エラー:', error);
        }
      }

      // 会話ログを準備
      const conversationLog = chatHistory.map((chat) => ({
        speaker: chat.sender === 'ai' ? 'interviewer' : 'candidate',
        message: chat.message,
        timestamp: chat.timestamp,
      }));

      // バックエンドに面接結果を送信（interviewIdが存在する場合）
      if (interviewId && conversationLog.length > 0) {
        try {

          // URL構築ロジック - 重複パス回避
          let API_BASE_URL: string;
          const ENV_API_URL = process.env.REACT_APP_API_BASE_URL;

          if (ENV_API_URL) {
            // 本番環境: URLが既に/api/v1で終わっているかチェック
            API_BASE_URL = ENV_API_URL.endsWith('/api/v1') ? ENV_API_URL : `${ENV_API_URL}/api/v1`;
          } else {
            // ローカル環境
            API_BASE_URL = 'http://localhost:3001/api/v1';
          }

          const requestUrl = `${API_BASE_URL}/interviews/${interviewId}/complete`;
          const requestBody = {
            conversationLog: conversationLog,
            // evaluationData: interviewData, // AI面接エージェントからの評価データ
            interviewDuration: Math.floor(timeElapsed / 60), // 分単位
            endTime: new Date().toISOString(),
          };


          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
          };
          if (candidateToken) {
            headers['Authorization'] = `Bearer ${candidateToken}`;
          }

          const completeResponse = await fetch(requestUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody),
          });


          const result = await completeResponse.json();

          if (completeResponse.ok && result.success) {

            // 成功メッセージをチャット履歴に追加
            setChatHistory((prev) => [
              ...prev,
              {
                sender: 'success',
                message: '面接結果が正常に保存されました。お疲れ様でした！',
                timestamp: new Date().toLocaleTimeString(),
              },
            ]);
          } else {
            console.error('❌ 面接結果のDB保存失敗:', {
              httpStatus: completeResponse.status,
              httpStatusText: completeResponse.statusText,
              responseOk: completeResponse.ok,
              resultSuccess: result.success,
              resultMessage: result.message,
              resultError: result.error,
              fullResult: result,
            });
            throw new Error(
              `面接結果保存失敗 [${completeResponse.status}]: ${result.message || result.error || 'レスポンス解析エラー'}`
            );
          }
        } catch (error) {
          console.error('❌ 面接結果保存エラー:', error);

          // エラーメッセージをチャット履歴に追加
          setChatHistory((prev) => [
            ...prev,
            {
              sender: 'system',
              message: `面接結果の保存中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
              timestamp: new Date().toLocaleTimeString(),
            },
          ]);
        }
      }
    } catch (error) {
      console.error('❌ 面接完了処理エラー:', error);

      // エラーメッセージをチャット履歴に追加
      setChatHistory((prev) => [
        ...prev,
        {
          sender: 'system',
          message: `面接完了処理中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } finally {
      // 最終処理
      setInterviewPhase('completed');
      setIsInterviewStarted(false);
      setIsSessionPrepared(false);

      stopCamera();
      stopSpeaking();
      if (isRecording && speechRecognition) {
        speechRecognition.stop();
        setIsRecording(false);
      }

    }
  };

  // endInterview関数は削除 - completeInterview関数に統合されました

  // 旧面接中止確認関数は削除（手動終了機能に統合）

  // 面接キャンセル確認モーダルを表示
  const showCancelConfirmation = (): void => {
    setShowCancelConfirmModal(true);
  };

  // 面接キャンセルをキャンセル
  const cancelCancelInterview = (): void => {
    setShowCancelConfirmModal(false);
    // キャンセル理由はリセットしない（ユーザーが再度開いた時に入力内容を保持）
  };

  // 面接手動終了確認モーダルを表示
  const showManualEndConfirmation = (): void => {
    setShowManualEndConfirmModal(true);
  };

  // 面接手動終了をキャンセル
  const cancelManualEndInterview = (): void => {
    setShowManualEndConfirmModal(false);
  };

  // 音声認識内容をリセット（現在未使用）
  // const resetTranscript = (): void => {
  //   setCurrentTranscript('');
  //   // 音声認識を停止
  //   if (isRecording && speechRecognition) {
  //     speechRecognition.stop();
  //     setIsRecording(false);
  //   }
  //   // 音声認識の自動再開を削除 - AI読み上げ完了後のみ再開するように変更
  // };

  // [認識中...] ラベルを除去し、暫定テキストも含めて返す
  const getRecognizedText = (text: string): string => {
    return text.replace(/\s*\[認識中\.\.\.\]\s*/g, ' ').trim();
  };

  // 回答送信
  const submitResponse = async (messageOverride?: string): Promise<void> => {

    // AIセッションIDの確認
    if (!aiSessionId) {
      console.error('❌ submitResponse: AIセッションIDが存在しません');
      const errorMessage: ChatMessage = {
        sender: 'system',
        message: 'AIセッションが開始されていません。面接を再開してください。',
        timestamp: new Date().toLocaleTimeString(),
      };
      setChatHistory((prev) => [...prev, errorMessage]);
      setIsUserTurn(true);
      return;
    }

    setIsUserTurn(false);
    // 暫定も含めてラベルだけ除去（オーバーライド優先）
    const responseText = getRecognizedText(messageOverride ?? currentTranscript);

    const newUserMessage: ChatMessage = {
      sender: 'user',
      message: responseText,
      timestamp: new Date().toLocaleTimeString(),
    };

    // ユーザーメッセージをチャット履歴に追加
    setChatHistory((prev) => [...prev, newUserMessage]);
    setCurrentTranscript('');

    // 録音停止
    if (isRecording && speechRecognition) {
      speechRecognition.stop();
      setIsRecording(false);
    }

    try {
      // AI思考中の段階的表示を開始
      setIsAIThinking(true);
      setIsUserTurn(false);
      setThinkingStage('listening');
      setThinkingProgress(0);

      // AI応答が到着するまでの段階的な思考演出。
      // 旧実装は setTimeout 内で state の isAIThinking を参照していたが、
      // クロージャが submitResponse 実行時点の古い値(false)を捕捉してしまい、
      // ステージが一切進まなかった。ローカルフラグで「応答到着前か」を判定する。
      let responseArrived = false;

      // ステージ1: 聞き取り（0-1秒）
      setTimeout(() => {
        if (!responseArrived) {
          setThinkingStage('understanding');
          setThinkingProgress(33);
        }
      }, 1000);

      // ステージ2: 理解（1-3秒）
      setTimeout(() => {
        if (!responseArrived) {
          setThinkingStage('preparing');
          setThinkingProgress(66);
        }
      }, 3000);

      // AI面接エージェントから応答を取得

      const rawAIResponse = await getAIResponse(responseText);
      responseArrived = true;

      // セクション完了マーカーを検出・除去
      const hasSectionComplete = rawAIResponse.includes(SECTION_COMPLETE_MARKER);
      const aiResponse = rawAIResponse.replace(SECTION_COMPLETE_MARKER, '').trim();
      if (hasSectionComplete) {
        pendingSectionCompleteRef.current = true;
      }


      // ステージ3: 準備完了
      setThinkingStage('ready');
      setThinkingProgress(100);

      // 少し待ってからUI更新
      setTimeout(() => {
        setIsAIThinking(false);

        const newAIMessage: ChatMessage = {
          sender: 'ai',
          message: aiResponse,
          timestamp: new Date().toLocaleTimeString(),
        };

        setChatHistory((prev) => [...prev, newAIMessage]);

        // AIの応答を読み上げ（遅延削除でパフォーマンス最適化）
        speakText(aiResponse);
      }, 500);
    } catch (error) {
      console.error('❌ AI応答取得エラー:', error);
      console.error('📊 エラー詳細:', {
        errorType: error instanceof Error ? error.name : typeof error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        aiSessionId,
        responseText: responseText.substring(0, 50) + '...',
      });

      // AI思考状態をリセット
      setIsAIThinking(false);
      setThinkingProgress(0);
      // エラー時にisUserTurnをtrueに戻さないと「回答を終了する」ボタンが
      // 二度と表示されず、候補者がそれ以上操作できなくなってしまう
      setIsUserTurn(true);

      // エラーメッセージを表示
      const errorMessage: ChatMessage = {
        sender: 'system',
        message: `AI面接官との通信エラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`,
        timestamp: new Date().toLocaleTimeString(),
      };

      setChatHistory((prev) => [...prev, errorMessage]);

      // 音声認識を再開（エラー時）
      setTimeout(() => {
        if (speechRecognition && !isRecording) {
          try {
            speechRecognition.start();
          } catch (restartError) {
            console.error('音声認識再開エラー:', restartError);
          }
        }
      }, 1000);
    }
  };

  // 面接再開ハンドラ
  const handleResumeSession = async (): Promise<void> => {
    if (!resumeData || !interviewId) return;
    setShowResumeModal(false);
    setIsAIThinking(true);

    try {
      // 過去の会話履歴を復元してUIに表示
      const history: ChatMessage[] = resumeData.messages.map((m) => ({
        sender: m.speaker === 'interviewer' ? 'ai' : 'user',
        message: m.message,
        timestamp: '',
      }));
      setChatHistory(history);
      setCurrentSection(resumeData.currentPhase);

      // エージェントにセッション再開を依頼
      const result = await aiInterviewService.resumeSession({
        backendSessionId: interviewId,
        currentPhase: resumeData.currentPhase as 'INTRO' | 'CAREER' | 'CASE' | 'CLOSING',
        existingMessages: resumeData.messages,
        candidateData: {
          company_id: companyId,
          company_name: companyName || '',
          industry,
          position,
          candidate_name: candidateName,
          current_industry: currentIndustry,
          current_position: currentPosition,
          is_experience: isExperienced,
          candidate_type: candidateType,
        },
      });

      if (result.success && result.data) {
        setAiSessionId(result.data.sessionId);
        setIsInterviewStarted(true);
        setInterviewPhase('active');

        // CLOSING 途中で再開した場合も締めの発言に完了マーカーが付くため、
        // ここでも検出・除去する（検出時は TTS 終了後に自動終了）。
        const rawOpening = result.data.openingMessage;
        const hasSectionComplete = rawOpening.includes(SECTION_COMPLETE_MARKER);
        const openingMessage = rawOpening.replace(SECTION_COMPLETE_MARKER, '').trim();
        if (hasSectionComplete) {
          pendingSectionCompleteRef.current = true;
        }
        const aiMessage: ChatMessage = {
          sender: 'ai',
          message: openingMessage,
          timestamp: new Date().toLocaleTimeString(),
        };
        setChatHistory((prev) => [...prev, aiMessage]);
        speakText(openingMessage);
      } else {
        setIsAIThinking(false);
        setShowResumeModal(true);
        setAiError(normalizeAIError(result.error || '面接の再開に失敗しました'));
      }
    } catch (error) {
      console.error('❌ セッション再開エラー:', error);
      setIsAIThinking(false);
      setShowResumeModal(true);
      setAiError(
        normalizeAIError(error instanceof Error ? error.message : '面接の再開に失敗しました')
      );
    }
  };

  // パート遷移ハンドラ
  const handleAdvanceSection = async (): Promise<void> => {
    const currentIdx = INTERVIEW_SECTIONS.findIndex((s) => s.id === currentSection);
    if (currentIdx >= INTERVIEW_SECTIONS.length - 1) return;

    // chatHistoryはこの後すぐクリアされるため、送信用に退避しておく
    const conversationLogAtTransition = chatHistory
      .filter((m) => m.sender === 'ai' || m.sender === 'user')
      .map((m) => ({
        speaker: m.sender === 'ai' ? 'interviewer' : 'candidate',
        message: m.message,
        timestamp: new Date().toISOString(),
      }));

    setShowAdvanceConfirm(false);
    setIsPhaseTransitioning(true);

    const next = INTERVIEW_SECTIONS[currentIdx + 1];

    // ① アニメーション後に即座に画面を次フェーズへ切り替え
    await new Promise((resolve) => setTimeout(resolve, 800));

    setCurrentSection(next.id);
    setChatHistory([]);
    setCurrentTranscript('');
    setIsUserTurn(false);
    setIsAIThinking(true); // API完了まで「考え中...」表示
    setIsPhaseTransitioning(false);

    // ② バックエンド・エージェントへの更新はバックグラウンドで実行
    try {
      if (interviewId && candidateToken) {
        await interviewApi.advancePhase(
          interviewId,
          {
            nextPhase: next.id as 'INTRO' | 'CAREER' | 'CASE' | 'CLOSING',
            conversationLog: conversationLogAtTransition,
          },
          candidateToken
        );
      }

      if (aiSessionId) {
        const result = await aiInterviewService.advancePhase(
          aiSessionId,
          next.id as 'INTRO' | 'CAREER' | 'CASE' | 'CLOSING'
        );
        if (result.success && result.data?.openingMessage) {
          // CLOSING の締めの発言はフェーズ遷移直後の openingMessage として届くため、
          // sendMessage 経路と同様にここでも完了マーカーを検出する。
          // CLOSING で検出された場合は TTS 終了後に自動で面接終了処理が走る
          // （面接終了ボタンは検出漏れ時のフォールバック）。
          const rawOpening = result.data.openingMessage;
          const hasSectionComplete = rawOpening.includes(SECTION_COMPLETE_MARKER);
          const openingMessage = rawOpening.replace(SECTION_COMPLETE_MARKER, '').trim();
          if (hasSectionComplete) {
            pendingSectionCompleteRef.current = true;
          }
          const aiMessage: ChatMessage = {
            sender: 'ai',
            message: openingMessage,
            timestamp: new Date().toLocaleTimeString(),
          };
          setChatHistory([aiMessage]);
          speakText(openingMessage);
          // isAIThinking は TTS 終了コールバックで false になる
          return;
        }
      }

      // 開始メッセージが取得できなかった場合
      setIsAIThinking(false);
    } catch (error) {
      console.error('❌ フェーズ遷移エラー:', error);
      setIsAIThinking(false);
    }
  };

  // 時間フォーマット
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // オーバーレイ可視状態に応じた下部スペースを事前確保
  // 下部バー表示条件（テロップ・ユーザーターン）
  const isTelopVisible =
    isInterviewStarted &&
    (currentSpeechText || lastInterviewerText) &&
    interviewPhase !== 'completed' &&
    interviewPhase !== 'processing' &&
    interviewPhase !== 'cancelled';
  const isUserTurnVisible =
    isInterviewStarted &&
    isUserTurn &&
    !currentSpeechText &&
    !isAIThinking &&
    interviewPhase !== 'completed' &&
    interviewPhase !== 'processing' &&
    interviewPhase !== 'cancelled';

  // AI思考中のステータスメッセージを取得
  const getThinkingMessage = (): string => {
    switch (thinkingStage) {
      case 'listening':
        return '考えています...';
      case 'understanding':
        return 'AI面接を準備中...';
      case 'preparing':
        return '最適な質問を準備中...';
      case 'ready':
        return '準備完了！';
      default:
        return '考えています...';
    }
  };

  // 右クリックメニューを無効化
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  // キーボードショートカットを無効化
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+S, F12などを無効化
    if (
      e.ctrlKey &&
      (e.key === 'a' || e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 's')
    ) {
      e.preventDefault();
      return false;
    }
    // F12 (開発者ツール)を無効化
    if (e.key === 'F12') {
      e.preventDefault();
      return false;
    }
    // Ctrl+Shift+I (開発者ツール)を無効化
    if (e.ctrlKey && e.shiftKey && e.key === 'I') {
      e.preventDefault();
      return false;
    }
    // Ctrl+U (ソース表示)を無効化
    if (e.ctrlKey && e.key === 'u') {
      e.preventDefault();
      return false;
    }
  };

  // 音声読み上げ停止
  const stopSpeaking = (): void => {
    if (speechSynthesisRef.current) {
      speechSynthesisRef.current.stop();
    }
    // 読み上げテキストもクリア
    setCurrentSpeechText('');
  };

  // マイクONなどのトリガで音声認識を安全に再開する
  // const restartRecognition = useCallback((reason: string = 'auto'): void => {
  //   const recognition = speechRecognitionRef.current;
  //   const synth = speechSynthesisRef.current;
  //   const aiSpeaking = isSpeaking || (synth?.isSpeaking || false);

  //   const canStart = !!recognition &&
  //     !aiSpeaking &&
  //     (isInterviewStarted || interviewPhase === 'active' || interviewPhase === 'waiting') &&
  //     interviewPhase !== 'completed';

  //   if (!canStart) return;

  //   if (!isRecording && recognition) {
  //     try {
  //       recognition.start();
  //     } catch (error: any) {
  //       if (error?.name === 'InvalidStateError') {
  //         console.log('🎤 音声認識は既に開始済み - 状態を同期');
  //         setIsRecording(true);
  //       } else {
  //         console.warn('🎤 音声認識開始エラー。少し待って再試行します', error);
  //         setTimeout(() => {
  //           try {
  //             recognition.start();
  //           } catch (e: any) {
  //             if (e?.name === 'InvalidStateError') {
  //               setIsRecording(true);
  //             } else {
  //               console.error('🎤 再試行でも音声認識開始に失敗', e);
  //             }
  //           }
  //         }, 300);
  //       }
  //     }
  //   }
  // }, [isRecording, isSpeaking, isInterviewStarted, interviewPhase]);

  // カメラの初期化と開始
  const startCamera = useCallback(async (): Promise<void> => {
    try {
      setCameraError('');

      // 既存のストリームを停止
      if (videoStream) {
        videoStream.getTracks().forEach((track) => track.stop());
        setVideoStream(null);
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user',
        },
        audio: false,
      });

      // ブラウザ設定からカメラ権限がOFFにされるとブラウザがトラックを終了させる。
      // これを検知して面接を一時停止する。
      stream.getVideoTracks().forEach((track) => {
        track.onended = () => {
          setPermissionBlocked('camera');
        };
      });

      setVideoStream(stream);

      // videoRef.currentがnullの場合はリトライ
      const setVideoStreamToRef = (retry = 0) => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          const handleLoadedMetadata = () => {
            if (videoRef.current) {
              videoRef.current.play().catch(() => {});
            }
          };
          if (videoRef.current.readyState >= 1) {
            handleLoadedMetadata();
          } else {
            videoRef.current.addEventListener('loadedmetadata', handleLoadedMetadata, {
              once: true,
            });
          }
        } else if (retry < 10) {
          setTimeout(() => setVideoStreamToRef(retry + 1), 100);
        } else {
          console.error('🎥 ビデオ要素の参照が見つかりません（リトライ上限）');
        }
      };
      setVideoStreamToRef();
    } catch (error) {
      setCameraError('カメラにアクセスできません。ブラウザの設定を確認してください。');
    }
  }, [videoStream]);

  // カメラ/マイクの権限が失効している間、権限が戻り次第自動的に面接を再開する
  useEffect(() => {
    if (!permissionBlocked) return;

    const interval = setInterval(async () => {
      try {
        const result = await navigator.permissions.query({
          name: permissionBlocked as PermissionName,
        });
        if (result.state !== 'granted') return;

        if (permissionBlocked === 'camera') {
          await startCamera();
        } else {
          try {
            speechRecognitionRef.current?.start();
          } catch (error: any) {
            if (error?.name !== 'InvalidStateError') {
              return; // 再開に失敗した場合はブロックを継続する
            }
          }
        }
        setPermissionBlocked(null);
      } catch {
        // permissions API が使えない場合は何もしない（対応ブラウザはChromeのみ）
      }
    }, 1500);

    return () => clearInterval(interval);
  }, [permissionBlocked, startCamera]);

  // videoStreamが更新された時にvideoRef.currentにセット
  useEffect(() => {
    if (videoStream && videoRef.current) {
      videoRef.current.srcObject = videoStream;
      videoRef.current.play().catch(() => {});
    }
  }, [videoStream]);

  // 面接開始時にカメラを自動起動
  useEffect(() => {
    if (isInterviewStarted && videoEnabled && !videoStream) {
      startCamera();
    }
  }, [isInterviewStarted, videoEnabled, videoStream, startCamera]);

  // 初回マウント時にカメラを自動起動（待機画面でも表示）
  useEffect(() => {

    if (videoEnabled && !videoStream && !cameraError) {
      const timer = setTimeout(() => {
        startCamera();
      }, 1000); // 1秒待ってから起動

      return () => clearTimeout(timer);
    }
  }, [videoEnabled, videoStream, isInterviewStarted, cameraError, startCamera]);

  // 音声波形アニメーション
  useEffect(() => {
    if (isSpeaking) {
      waveformIntervalRef.current = setInterval(() => {
        setWaveformHeights((prev) => prev.map(() => 8 + Math.random() * 20));
      }, 150);
    } else {
      if (waveformIntervalRef.current) {
        clearInterval(waveformIntervalRef.current);
        waveformIntervalRef.current = null;
      }
      // 波形を初期状態にリセット
      setWaveformHeights([12, 16, 20, 14, 18]);
    }

    return () => {
      if (waveformIntervalRef.current) {
        clearInterval(waveformIntervalRef.current);
      }
    };
  }, [isSpeaking]);

  // AI発話中の停止と、ユーザーターン時の自動再開を一元制御
  useEffect(() => {
    const recognition = speechRecognitionRef.current;
    // 読み上げ終了通知(isSpeaking=false)後も speechSynthesis.isSpeaking(getter)は
    // しばらく true を返すラグがある。これを見ると、読み上げ終了直後に即再開した
    // 音声認識を直後の stop で打ち消してしまい「読み切った直後の発話が拾われない」
    // 原因になる。AI発話中の判定は state の isSpeaking のみで行う。
    const isAISpeaking = isSpeaking;
    const canAutoRestartRecognition =
      isInterviewStarted &&
      isUserTurn &&
      !isAIThinking &&
      currentSection !== 'CLOSING' &&
      (interviewPhase === 'active' || interviewPhase === 'waiting');

    if (!recognition) return;

    // AIが話中は音声認識を停止
    if (isAISpeaking) {
      if (isRecording) {
        recognition.stop();
        setIsRecording(false);
      }
      return;
    }

    // ユーザーターン中に無音終了で止まった場合は自動再開
    if (canAutoRestartRecognition && !isRecording) {
      if (speechRestartTimeoutRef.current) {
        clearTimeout(speechRestartTimeoutRef.current);
      }

      speechRestartTimeoutRef.current = setTimeout(() => {
        const currentRecognition = speechRecognitionRef.current;
        const currentSpeechSynthesis = speechSynthesisRef.current;
        const isAIStillSpeaking = isSpeaking || currentSpeechSynthesis?.isSpeaking || false;

        if (!currentRecognition || isAIStillSpeaking) {
          speechRestartTimeoutRef.current = null;
          return;
        }

        try {
          currentRecognition.start();
        } catch (error: any) {
          if (error?.name === 'InvalidStateError') {
            setIsRecording(true);
          } else {
            console.warn('🎤 音声認識の自動再開に失敗', error);
          }
        } finally {
          speechRestartTimeoutRef.current = null;
        }
      }, 400);
    }

    if (!canAutoRestartRecognition && speechRestartTimeoutRef.current) {
      clearTimeout(speechRestartTimeoutRef.current);
      speechRestartTimeoutRef.current = null;
    }

    return () => {
      if (speechRestartTimeoutRef.current) {
        clearTimeout(speechRestartTimeoutRef.current);
        speechRestartTimeoutRef.current = null;
      }
    };
  }, [
    isSpeaking,
    isRecording,
    isInterviewStarted,
    isUserTurn,
    isAIThinking,
    interviewPhase,
    currentSection,
    speechRecognition,
  ]);

  // audioEnabledの状態変更を監視して音声認識を制御
  // audioEnabled を廃止したため関連の副作用は削除

  // カメラの停止
  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      videoRef.current.load();
    }
  }, []);

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      stopCamera();
      if (speechSynthesisRef.current) {
        speechSynthesisRef.current.dispose();
      }
      // 音声認識を確実に停止し、unmount 後にハンドラが state を更新しないよう解除する
      const recognition = speechRecognitionRef.current;
      if (recognition) {
        recognition.onstart = null;
        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
        try {
          // ハンドラ解除済みのため stop 後の結果/onend は無視される
          recognition.stop();
        } catch {
          // 既に停止済みの場合は無視
        }
      }
      // スクリーンショットキャプチャのリソースを解放（dispose 内で cancelCapture も実行される）
      if (screenshotCaptureRef.current) {
        screenshotCaptureRef.current.dispose();
      }
      // 残存タイマーの解放（最後のレンダーで貼られたタイマーが unmount 後に発火しないように）
      if (speechRestartTimeoutRef.current) {
        clearTimeout(speechRestartTimeoutRef.current);
        speechRestartTimeoutRef.current = null;
      }
      if (waveformIntervalRef.current) {
        clearInterval(waveformIntervalRef.current);
        waveformIntervalRef.current = null;
      }
    };
  }, [stopCamera]);

  // 面接データ処理中はブラウザを閉じさせないようにする
  useEffect(() => {
    const isProcessing = showEndingNotification || interviewPhase === 'processing';

    if (!isProcessing) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [showEndingNotification, interviewPhase]);

  return (
    <div
      className="h-dvh flex flex-col overflow-hidden bg-gray-50 no-select no-context-menu no-drag"
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      onContextMenu={handleContextMenu}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* ローディングモーダル */}
      {isLoadingSessionStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">AI面接を準備中...</p>
            </div>
          </div>
        </div>
      )}

      {/* 面接終了済み・キャンセル済みモーダル */}
      {!isLoadingSessionStatus &&
        (interviewSessionStatus === 'completed' ||
          interviewSessionStatus === 'evaluated' ||
          interviewSessionStatus === 'cancelled') && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div
                className={`${interviewSessionStatus === 'cancelled' ? 'bg-gradient-to-r from-orange-600 to-red-600' : 'bg-gradient-to-r from-green-600 to-blue-600'} text-white p-6 rounded-t-lg`}
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                    {interviewSessionStatus === 'cancelled' ? (
                      <PhoneOff className="w-8 h-8 text-white" />
                    ) : (
                      <svg
                        className="w-8 h-8 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  <h2 className="text-2xl font-bold mb-2">
                    {interviewSessionStatus === 'cancelled' ? '面接キャンセル' : '面接終了'}
                  </h2>
                  <p
                    className={`${interviewSessionStatus === 'cancelled' ? 'text-orange-100' : 'text-green-100'}`}
                  >
                    {candidateName}様の面接は
                    {interviewSessionStatus === 'cancelled'
                      ? 'キャンセルされています'
                      : '既に完了しています'}
                  </p>
                </div>
              </div>

              <div className="p-6">
                <div
                  className={`${interviewSessionStatus === 'cancelled' ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'} rounded-lg p-4 mb-4`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      {interviewSessionStatus === 'cancelled' ? (
                        <PhoneOff className="h-5 w-5 text-orange-400" />
                      ) : (
                        <svg
                          className="h-5 w-5 text-green-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="ml-3">
                      <h3
                        className={`text-sm font-medium ${interviewSessionStatus === 'cancelled' ? 'text-orange-800' : 'text-green-800'} mb-1`}
                      >
                        面接状況
                      </h3>
                      <div
                        className={`text-sm ${interviewSessionStatus === 'cancelled' ? 'text-orange-700' : 'text-green-700'}`}
                      >
                        <p className="mb-2">
                          ステータス:{' '}
                          <span className="font-semibold">
                            {interviewSessionStatus === 'cancelled'
                              ? 'キャンセル済み'
                              : interviewSessionStatus === 'completed'
                                ? '面接完了（評価処理中）'
                                : '評価完了'}
                          </span>
                        </p>
                        {interviewSessionStatus === 'cancelled' && (
                          <p className="text-orange-700">
                            ❌ 面接がキャンセルされました。評価は行われません。
                          </p>
                        )}
                        {interviewSessionStatus === 'completed' && (
                          <p className="text-yellow-700">
                            ⚡ AI評価が進行中です。しばらくお待ちください。
                          </p>
                        )}
                        {interviewSessionStatus === 'evaluated' && (
                          <p className="text-green-700">
                            ✓ 評価が完了しました。結果は採用担当者が確認します。
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-medium text-blue-800 mb-2">今後の流れ</h3>
                  <ul className="text-sm text-blue-700 space-y-1">
                    {interviewSessionStatus === 'cancelled' ? (
                      <>
                        <li>• 面接がキャンセルされたため、評価は行われません</li>
                        <li>• 会話ログは保存されています</li>
                        <li>• 再面接をご希望の場合は採用担当者にお問い合わせください</li>
                      </>
                    ) : (
                      <>
                        <li>• 採用担当者が面接結果を確認します</li>
                        <li>• 結果についての連絡をお待ちください</li>
                        <li>• ご不明な点があれば採用担当者にお問い合わせください</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* 面接説明モーダル */}
      {showInstructionModal &&
        !isInterviewStarted &&
        !isLoadingSessionStatus &&
        interviewSessionStatus !== 'completed' &&
        interviewSessionStatus !== 'evaluated' &&
        interviewSessionStatus !== 'cancelled' && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" style={{ backdropFilter: 'blur(4px)' }}>
            {/* パネル自体を画面高に収める。以前は本文だけ max-h-[60vh] で、
                ヘッダー＋本文＋フッターの合計が画面を超えて上下が切れていた。 */}
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-full flex flex-col overflow-hidden">

              {/* ヘッダー */}
              <div className="bg-gradient-to-br from-green-500 to-emerald-700 px-8 pt-7 pb-6 text-center relative overflow-hidden flex-shrink-0">
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-96 h-96 rounded-full border-2 border-white border-opacity-10"></div>
                </div>
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Bot className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-white text-2xl font-bold mb-1">Talent Scan AI面接にようこそ</h2>
                <p className="text-green-100 text-sm">
                  {candidateName} さん・{position}
                </p>
              </div>

              <div className="px-8 py-6 flex-1 min-h-0 overflow-y-auto space-y-6">

                {/* エラー表示 */}
                {aiError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-sm font-medium text-red-700 mb-3">{aiError}</p>
                    <div className="flex space-x-2">
                      <button onClick={() => window.location.reload()} className="text-sm px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">ページを再読み込み</button>
                      <button onClick={() => { setAiError(''); prepareAISession(); }} className="text-sm px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors">再接続を試す</button>
                    </div>
                  </div>
                )}

                {/* システム確認 */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-1 h-6 bg-green-500 rounded-full"></div>
                    <h3 className="text-base font-bold text-gray-800">システム確認</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      {
                        icon: '📷',
                        label: 'カメラ',
                        status: cameraCheckStatus,
                      },
                      {
                        icon: '🎤',
                        label: 'マイク',
                        status: micCheckStatus,
                      },
                      {
                        icon: '🌐',
                        label: 'AI接続',
                        status: (aiError ? 'error' : isSessionPrepared ? 'ok' : 'checking') as CheckStatus,
                      },
                    ].map((item) => (
                      <div key={item.label} className="flex flex-col items-center justify-center bg-gray-50 rounded-2xl py-4 px-3 space-y-2">
                        <span className="text-2xl">{item.icon}</span>
                        <span className="text-sm font-medium text-gray-700">{item.label}</span>
                        {item.status === 'checking' && (
                          <div className="flex items-center space-x-1.5">
                            <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-xs text-gray-400">確認中</span>
                          </div>
                        )}
                        {item.status === 'ok' && (
                          <div className="flex items-center space-x-1">
                            <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <span className="text-xs text-green-600 font-medium">OK</span>
                          </div>
                        )}
                        {item.status === 'error' && (
                          <div className="flex items-center space-x-1">
                            <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <span className="text-xs text-red-600 font-medium">エラー</span>
                          </div>
                        )}
                        {item.status === 'unknown' && (
                          <div className="flex items-center space-x-1">
                            <div className="w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold leading-none">!</span>
                            </div>
                            <span className="text-xs text-yellow-600 font-medium">要確認</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {(cameraCheckStatus === 'error' || micCheckStatus === 'error') && (
                    <p className="mt-3 text-xs text-red-600 pl-1">
                      ⚠️ カメラまたはマイクへのアクセスが拒否されています。ブラウザのアドレスバー左のアイコンから許可してください。
                    </p>
                  )}
                  {(cameraCheckStatus === 'unknown' || micCheckStatus === 'unknown') && (
                    <p className="mt-3 text-xs text-yellow-600 pl-1">
                      ⚠️ 面接開始時にカメラ・マイクの許可ダイアログが表示されます。「許可」を選択してください。
                    </p>
                  )}
                </div>

                <hr className="border-gray-100" />

                {/* 事前準備 */}
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-1 h-6 bg-green-500 rounded-full"></div>
                    <h3 className="text-base font-bold text-gray-800">事前準備</h3>
                  </div>
                  <p className="text-base text-gray-600 leading-relaxed pl-4 mb-5">
                    面接開始後、まず <span className="font-semibold text-gray-800">「導入パート」</span> から始まります。AI面接官がシステムの使い方と面接の流れを説明しますので、案内に沿って進めてください。
                  </p>
                  <ul className="space-y-5 pl-4">
                    {[
                      {
                        icon: '🔇',
                        title: '静かな環境で受ける',
                        detail: '周囲の雑音は音声認識の精度に影響します。できるだけ静かな部屋で受けてください。',
                      },
                      {
                        icon: '🎧',
                        title: 'ヘッドセット・イヤホンを使用する',
                        detail: 'スピーカーで音声を流すとAIの声がマイクに入り、認識精度が下がる場合があります。イヤホンの使用を推奨します。',
                      },
                      {
                        icon: '💡',
                        title: '明るい場所に座る',
                        detail: '照明が正面から当たる場所が最適です。逆光（窓を背にする向き）は避けてください。',
                      },
                    ].map((item) => (
                      <li key={item.title} className="flex items-start space-x-4">
                        <span className="flex-shrink-0 text-2xl mt-0.5">{item.icon}</span>
                        <div>
                          <p className="text-base font-semibold text-gray-800 mb-1">{item.title}</p>
                          <p className="text-sm text-gray-500 leading-relaxed">{item.detail}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <hr className="border-gray-100" />

                {/* 注意事項 */}
                <div>
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-1 h-6 bg-gray-400 rounded-full"></div>
                    <h3 className="text-base font-bold text-gray-800">注意事項</h3>
                  </div>
                  <ul className="space-y-2.5 pl-4">
                    {[
                      '面接中はカメラと音声が録画されます',
                      '面接中の画面操作（右クリック等）は制限されています',
                      'ブラウザを閉じると面接データが失われる可能性があります',
                      '安定したインターネット接続をご確認ください',
                      'AI面接官の発言は画面下部にテキストでも表示されます',
                    ].map((text) => (
                      <li key={text} className="flex items-center space-x-2.5 text-sm text-gray-500">
                        <span className="flex-shrink-0 w-1.5 h-1.5 bg-gray-300 rounded-full"></span>
                        <span>{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>

              </div>

              {/* 開始ボタン（固定フッター） */}
              <div className="px-8 pb-6 pt-3 flex-shrink-0 border-t border-gray-100">
                {(() => {
                  const { canStart, isChecking, blockers } = checkInterviewStartConditions({
                    isSessionPrepared,
                    aiError,
                    cameraCheckStatus,
                    micCheckStatus,
                  });
                  const checking = isChecking;

                  return (
                    <>
                      <button
                        onClick={startInterview}
                        disabled={!canStart}
                        className={`w-full py-4 rounded-2xl text-base font-bold transition-all flex items-center justify-center space-x-2 ${
                          !canStart
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg shadow-green-200'
                        }`}
                      >
                        {checking ? (
                          <>
                            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                            <span>確認中...</span>
                          </>
                        ) : (
                          <>
                            <span>面接を始める</span>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </>
                        )}
                      </button>
                      {blockers.length > 0 && (
                        <p className="mt-3 text-sm text-red-500 text-center">
                          {blockers.join('・')} の問題を解決してから始めてください
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

      {/* 旧面接完了確認モーダルは削除（手動終了機能に統合） */}

      {/* 面接キャンセル確認モーダル */}
      {showCancelConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-6 rounded-t-lg">
              <h2 className="text-xl font-bold mb-2">面接をキャンセルしますか？</h2>
              <p className="text-orange-100">面接を中止し、評価は行われません</p>
            </div>

            <div className="p-6">
              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <PhoneOff className="h-5 w-5 text-orange-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-orange-700">
                      <strong>キャンセル後の処理:</strong>{' '}
                      録画データの保存、会話ログの保存を行いますが、AI評価は生成されません。
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  キャンセル理由 <span className="text-red-500">*</span>
                </h3>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="キャンセル理由をご記入ください（必須）"
                  className={`w-full p-3 border rounded-lg resize-none h-20 text-sm ${
                    cancelReason.trim() === ''
                      ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
                  } focus:outline-none focus:ring-2`}
                  maxLength={500}
                  required
                />
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-500">{cancelReason.length}/500文字</p>
                  {cancelReason.trim() === '' && (
                    <p className="text-xs text-red-500">※ キャンセル理由の入力は必須です</p>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-gray-900 mb-2">現在の進捗</h4>
                <div className="text-sm text-gray-600">
                  <div>経過時間: {formatTime(timeElapsed)}</div>
                  <div>会話回数: {Math.floor(chatHistory.length / 2)}回</div>
                </div>
              </div>
            </div>

            {/* ボタンエリア */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-between items-center">
              <button
                onClick={cancelCancelInterview}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                面接を続ける
              </button>
              <button
                onClick={() => cancelInterview('ユーザーが面接をキャンセルしました')}
                disabled={cancelReason.trim() === ''}
                className={`px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                  cancelReason.trim() === ''
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                <PhoneOff className="w-4 h-4" />
                <span>面接をキャンセルする</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 面接手動終了確認モーダル */}
      {showManualEndConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white p-6 rounded-t-lg">
              <h2 className="text-xl font-bold mb-2">面接を終了しますか？</h2>
              <p className="text-blue-100">面接を完了し、AI評価を生成します</p>
            </div>

            <div className="p-6">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>重要:</strong> 面接を終了すると再開できません。本当によろしいですか？
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">面接終了後の処理</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• 録画データが自動的に保存されます</li>
                  <li>• AI評価レポートが生成されます</li>
                  <li>• 面接結果がデータベースに保存されます</li>
                  <li>• 完了後は結果を確認できます</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-gray-900 mb-2">現在の進捗</h4>
                <div className="text-sm text-gray-600">
                  <div>経過時間: {formatTime(timeElapsed)}</div>
                  <div>会話回数: {Math.floor(chatHistory.length / 2)}回</div>
                </div>
              </div>
            </div>

            {/* ボタンエリア */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-between items-center">
              <button
                onClick={cancelManualEndInterview}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                面接を続ける
              </button>
              <button
                onClick={() => {
                  setShowManualEndConfirmModal(false);
                  completeInterview('手動で面接を終了しました');
                }}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
              >
                <PhoneOff className="w-4 h-4" />
                <span>面接を終了する</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 再開モーダル */}
      {showResumeModal && resumeData && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" style={{ backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="bg-gradient-to-br from-blue-500 to-indigo-700 px-8 pt-10 pb-8 text-center relative overflow-hidden">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-white text-2xl font-bold mb-1">面接が途中になっています</h2>
              <p className="text-blue-100 text-sm">続きから再開できます</p>
            </div>

            <div className="px-8 py-7">
              <div className="bg-gray-50 rounded-2xl p-4 mb-6 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">中断したパート</span>
                  <span className="font-semibold text-gray-800">
                    {INTERVIEW_SECTIONS.find(s => s.id === resumeData.currentPhase)?.label ?? resumeData.currentPhase}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">会話数</span>
                  <span className="font-semibold text-gray-800">{resumeData.messages.length} 件</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">最終保存</span>
                  <span className="font-semibold text-gray-800">
                    {new Date(resumeData.updatedAt).toLocaleString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>

              {/* AI接続の状態。これが無いと、準備中はボタンが薄くなるだけで
                  「なぜ押せないのか」が候補者に伝わらない（故障に見える）。 */}
              {aiError ? (
                <div className="mb-3 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 flex items-start space-x-2">
                  <span className="text-red-500 mt-0.5">⚠️</span>
                  <div>
                    <p className="text-sm font-semibold text-red-800">AIに接続できませんでした</p>
                    <p className="text-xs text-red-600 mt-0.5">{aiError}</p>
                    <p className="text-xs text-red-600 mt-1">
                      通信環境をご確認のうえ、ページを再読み込みしてください。
                    </p>
                  </div>
                </div>
              ) : !isSessionPrepared ? (
                <div className="mb-3 bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                  <p className="text-sm text-blue-800">
                    AIに接続しています。完了すると再開できます。
                  </p>
                </div>
              ) : null}

              <button
                onClick={handleResumeSession}
                disabled={!isSessionPrepared}
                className="w-full py-4 mb-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-2xl text-base font-bold transition-all shadow-lg shadow-blue-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSessionPrepared ? (
                  <>
                    <span>続きから再開する</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                ) : (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>準備しています...</span>
                  </>
                )}
              </button>

              {!showRestartConfirm ? (
                <button
                  onClick={() => setShowRestartConfirm(true)}
                  className="w-full py-3 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                >
                  最初からやり直す
                </button>
              ) : (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-2xl p-4">
                  <p className="text-sm font-semibold text-red-800 mb-1">
                    本当に最初からやり直しますか？
                  </p>
                  <p className="text-xs text-red-600 mb-3">
                    これまでの会話（{resumeData.messages.length} 件）は削除され、元に戻せません。
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={async () => {
                        // やり直しは明示的な意思なので、この時点で会話ログを初期化して
                        // フェーズを導入に戻す（初期化に失敗したらモーダルに留まる）
                        try {
                          const resetResult = await interviewApi.resetInterviewConversation(
                            interviewId!,
                            candidateToken
                          );
                          if (!resetResult.success) {
                            setAiError(normalizeAIError(resetResult.message || '会話ログの初期化に失敗しました'));
                            return;
                          }
                        } catch (e) {
                          setAiError(normalizeAIError('会話ログの初期化に失敗しました'));
                          return;
                        }
                        setShowRestartConfirm(false);
                        setResumeData(null);
                        setShowResumeModal(false);
                        setShowInstructionModal(true);
                      }}
                      className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold transition-colors"
                    >
                      削除してやり直す
                    </button>
                    <button
                      onClick={() => setShowRestartConfirm(false)}
                      className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition-colors"
                    >
                      戻る
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* フェーズ遷移オーバーレイ */}
      {isPhaseTransitioning && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">
              {INTERVIEW_SECTIONS[INTERVIEW_SECTIONS.findIndex((s) => s.id === currentSection) + 1]?.label ?? ''}パートに移ります
            </h3>
            <p className="text-sm text-gray-500">しばらくお待ちください...</p>
          </div>
        </div>
      )}

      {/* パート終了 → 次パートへ進む モーダル */}
      {showAdvanceConfirm && (() => {
        const currentIdx = INTERVIEW_SECTIONS.findIndex((s) => s.id === currentSection);
        const current = INTERVIEW_SECTIONS[currentIdx];
        const next = INTERVIEW_SECTIONS[currentIdx + 1];
        if (!next) return null;
        return (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" style={{ backdropFilter: 'blur(4px)' }}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">

              {/* 上部グラデーションヘッダー */}
              <div className="bg-gradient-to-br from-green-500 to-emerald-700 px-8 pt-10 pb-8 text-center relative overflow-hidden">
                {/* 背景の装飾リング */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-64 h-64 rounded-full border-2 border-white border-opacity-10"></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-48 h-48 rounded-full border-2 border-white border-opacity-10"></div>
                </div>

                {/* 完了アイコン */}
                <div className="relative w-20 h-20 mx-auto mb-4">
                  <div className="absolute inset-0 bg-white bg-opacity-20 rounded-full animate-ping"></div>
                  <div className="relative w-20 h-20 bg-white bg-opacity-25 rounded-full flex items-center justify-center">
                    <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>

                <p className="text-white text-opacity-80 text-sm font-medium mb-1">パート完了</p>
                <h2 className="text-white text-3xl font-bold tracking-tight">{current.label}</h2>

                {/* ステップインジケーター */}
                <div className="flex items-center justify-center mt-6 space-x-1">
                  {INTERVIEW_SECTIONS.map((s, i) => (
                    <React.Fragment key={s.id}>
                      <div className={`rounded-full transition-all ${
                        i < currentIdx ? 'w-2 h-2 bg-white' :
                        i === currentIdx ? 'w-8 h-2 bg-white' :
                        i === currentIdx + 1 ? 'w-4 h-2 bg-white bg-opacity-50' :
                        'w-2 h-2 bg-white bg-opacity-25'
                      }`} />
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* 次パートの情報 */}
              <div className="px-8 py-7">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">次のパート</p>

                <div className="flex items-start space-x-4 mb-6">
                  {/* 番号バッジ */}
                  <div className="flex-shrink-0 w-12 h-12 bg-green-50 rounded-2xl flex items-center justify-center border-2 border-green-100">
                    <span className="text-green-600 font-bold text-lg">{currentIdx + 2}</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">{next.label}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{next.nextDescription ?? next.description}</p>
                  </div>
                </div>

                {/* 進むボタン */}
                <button
                  onClick={handleAdvanceSection}
                  className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-2xl text-base font-bold transition-all shadow-lg shadow-green-200 flex items-center justify-center space-x-2"
                >
                  <span>{next.label}パートへ進む</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>

              </div>
            </div>
          </div>
        );
      })()}

      {/* モバイル対応ヘッダー */}
      {/* z-30: 万一スクロールが発生してもカメラ等のオーバーレイ(z-20)がヘッダーに被らないようにする */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-30 flex-shrink-0">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-green-600 rounded flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  {companyName ? `${companyName} 採用担当` : 'AI面接'}
                </h1>
                <div className="flex items-center space-x-2 text-xs text-gray-600">
                  <User className="w-3 h-3" />
                  <span>{candidateName}様</span>
                  <span className="text-gray-400">|</span>
                  <span>{position}職</span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {isInterviewStarted && (
                <div className="flex items-center space-x-2 text-sm">
                  <Clock className="w-4 h-4 text-gray-600" />
                  <span className="font-mono text-gray-700">{formatTime(timeElapsed)}</span>
                </div>
              )}

              {/* 面接終了ボタン（面接中のみ表示）。
                  以前は下部バーにあり、候補者の主操作「回答を終了する」と近接していて
                  押し間違えると面接が終わってしまうため、ヘッダーへ移した。 */}
              {isInterviewStarted && interviewPhase === 'active' && (
                <button
                  onClick={showManualEndConfirmation}
                  className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors flex items-center space-x-1"
                  title="面接を終了する"
                >
                  <PhoneOff className="w-3.5 h-3.5" />
                  <span>面接終了</span>
                </button>
              )}

              {/* 面接中止ボタン（面接中のみ表示） */}
              {isInterviewStarted && interviewPhase === 'active' && (
                <button
                  onClick={showCancelConfirmation}
                  className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 transition-colors"
                  title="面接をキャンセルする"
                >
                  やむを得ない理由により途中で中止する
                </button>
              )}
            </div>
          </div>

          {/* フェーズステッパー（面接中のみ） */}
          {isInterviewStarted && interviewPhase === 'active' && (
            <div className="mt-3 flex items-center">
              {INTERVIEW_SECTIONS.map((section, idx) => {
                const currentIdx = INTERVIEW_SECTIONS.findIndex((s) => s.id === currentSection);
                const isDone = idx < currentIdx;
                const isCurrent = idx === currentIdx;
                return (
                  <React.Fragment key={section.id}>
                    {/* ステップ */}
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                          isDone
                            ? 'bg-green-600 border-green-600 text-white'
                            : isCurrent
                            ? 'bg-white border-green-600 text-green-600'
                            : 'bg-white border-gray-300 text-gray-400'
                        }`}
                      >
                        {isDone ? (
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          idx + 1
                        )}
                      </div>
                      <span
                        className={`text-xs mt-0.5 font-medium ${
                          isCurrent ? 'text-green-600' : isDone ? 'text-green-500' : 'text-gray-400'
                        }`}
                      >
                        {section.label}
                      </span>
                    </div>
                    {/* コネクター */}
                    {idx < INTERVIEW_SECTIONS.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-1 mb-4 transition-colors ${
                          idx < currentIdx ? 'bg-green-500' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4 flex-1 flex flex-col min-h-0">
        {/* 面接終了通知モーダル */}
        {showEndingNotification && (
          <div className="fixed inset-0 bg-black bg-opacity-85 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* 警告ヘッダー */}
              <div className="bg-red-600 px-6 py-4 flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-7 h-7 text-white animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-bold text-base leading-tight">ブラウザを閉じないでください</p>
                  <p className="text-red-100 text-xs mt-0.5">データが失われる可能性があります</p>
                </div>
              </div>

              {/* 本文 */}
              <div className="px-6 py-6 text-center">
                <div className="relative w-16 h-16 mx-auto mb-4">
                  <div className="absolute inset-0 rounded-full bg-green-100 animate-ping opacity-50"></div>
                  <div className="relative w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>

                <h2 className="text-xl font-bold text-gray-900 mb-2">面接が終了しました</h2>
                <p className="text-sm text-gray-500 mb-5 leading-relaxed">{endingReason}</p>

                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-center space-x-3 text-blue-600 mb-3">
                    <svg className="animate-spin h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm font-medium">面接データを保存しています...</span>
                  </div>
                  <p className="text-xs text-gray-400">完了画面が表示されるまでこのままお待ちください</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI面接官表示エリア（画面高に収め、ビデオ領域だけを伸縮させる） */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden flex-1 flex flex-col min-h-0">
          <div className="relative flex-1 min-h-[240px] overflow-hidden">
            {/* AI面接官（フルスクリーン） */}
            <div className="relative w-full h-full flex items-center justify-center transition-all duration-500 bg-gradient-to-br from-green-800 via-emerald-800 to-green-900">
              {/* 話している時の背景アニメーション */}
              {isSpeaking && (
                <>
                  <div className="absolute inset-0 bg-gradient-to-br from-green-600 via-emerald-600 to-green-700 opacity-20 animate-pulse"></div>
                  <div className="absolute inset-0 bg-gradient-radial from-green-400 via-transparent to-transparent opacity-30 animate-pulse"></div>
                  {/* 話している時の光のリング */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-96 h-96 rounded-full bg-green-400 opacity-10 animate-ping"></div>
                  </div>
                </>
              )}

              <div className="text-center relative z-10 max-h-full overflow-y-auto py-4 px-4 w-full">
                {/* AI面接官の画像。領域が低いときは縮めて、見出し・発話スピード・
                    スキップが押し出されないようにする */}
                <div className="w-40 h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 max-h-[35vh] max-w-[35vh] mb-4 md:mb-6 mx-auto relative">
                  <img
                    src="/interview-man.png"
                    alt={companyName ? `${companyName} 採用担当` : 'AI面接官'}
                    className="w-full h-full object-cover rounded-full border-4 border-white shadow-lg"
                    onError={(e) => {
                      // 画像が読み込めない場合はフォールバックアイコンを表示
                      e.currentTarget.style.display = 'none';
                      const fallbackElement = e.currentTarget.nextElementSibling as HTMLElement;
                      if (fallbackElement) {
                        fallbackElement.style.display = 'flex';
                      }
                    }}
                  />
                  {/* フォールバックアイコン */}
                  <div
                    className="w-full h-full bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm absolute top-0 left-0 border-4 border-white shadow-lg"
                    style={{ display: 'none' }}
                  >
                    <Bot className="w-12 h-12 text-white" />
                  </div>
                </div>

                {/* 話している時の音声波形エフェクト */}
                {isSpeaking && (
                  <div className="mb-3">
                    <div className="flex items-center justify-center space-x-1 mb-2">
                      {[...Array(5)].map((_, index) => (
                        <div
                          key={index}
                          className="w-1.5 bg-green-400 rounded-full animate-pulse opacity-80"
                          style={{
                            height: `${16 + Math.sin(index) * 8}px`,
                            animationDelay: `${index * 0.15}s`,
                            animationDuration: `${0.6 + Math.random() * 0.2}s`,
                          }}
                        ></div>
                      ))}
                    </div>
                    {/* 音声インジケーター */}
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-8 h-0.5 bg-green-400 rounded-full animate-pulse opacity-70"></div>
                      <Mic className="w-4 h-4 text-green-400" />
                      <div className="w-8 h-0.5 bg-green-400 rounded-full animate-pulse opacity-70"></div>
                    </div>
                  </div>
                )}

                {/* AI思考中の表示 */}
                {isAIThinking && (
                  <div className="mb-2">
                    <div className="flex items-center justify-center space-x-1 mb-1">
                      <div
                        className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0s' }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0.1s' }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0.2s' }}
                      ></div>
                    </div>
                    <p className="text-blue-200 text-xs text-center">{getThinkingMessage()}</p>
                    {/* 長時間の場合のプログレスバー */}
                    {thinkingProgress > 0 && (
                      <div className="w-16 bg-blue-900 rounded-full h-1 mx-auto mt-1">
                        <div
                          className="bg-blue-400 h-1 rounded-full transition-all duration-300"
                          style={{ width: `${thinkingProgress}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                )}

                <h3 className="text-white text-xl md:text-2xl lg:text-3xl font-semibold">
                  {companyName ? `${companyName} 採用担当` : 'AI面接官'}
                </h3>
                <p
                  className={`text-base md:text-lg lg:text-xl mt-3 ${isAIThinking ? 'text-blue-200' : interviewPhase === 'cancelled' ? 'text-orange-200' : 'text-green-200'}`}
                >
                  {interviewPhase === 'waiting' && 'お待ちしております'}
                  {interviewPhase === 'active' &&
                    isAIThinking &&
                    !isSessionPrepared &&
                    'AI面接を準備中...'}
                  {interviewPhase === 'active' && isAIThinking && isSessionPrepared && '考え中...'}
                  {interviewPhase === 'active' && !isAIThinking && '面接中'}
                  {interviewPhase === 'processing' && '面接結果を処理中...'}
                  {interviewPhase === 'completed' && '面接終了'}
                  {interviewPhase === 'cancelled' && '面接キャンセル'}
                </p>
              </div>
            </div>

            {/* AI発話コントロール（左上オーバーレイ）。
                以前は中央ブロックの下に積んでいたため、テロップが伸びると
                下端が隠れて操作できなくなっていた。右上のカメラと対の位置に出す。 */}
            {isInterviewStarted && interviewPhase === 'active' && (
              <div className="absolute top-4 left-4 z-20 flex flex-col items-start bg-black/30 backdrop-blur-sm rounded-xl px-3 py-2">
                <span className="text-white/70 text-xs mb-1">発話スピード</span>
                <div className="relative inline-flex bg-white/20 rounded-lg p-[3px]">
                    <div
                      className="absolute top-[3px] bottom-[3px] bg-white/30 rounded-md transition-all duration-200 ease-in-out"
                      style={{
                        width: 'calc(100% / 3 - 2px)',
                        left: speechSpeed === 'slow' ? '3px' : speechSpeed === 'normal' ? 'calc(100% / 3 + 1px)' : 'calc(200% / 3 - 1px)',
                      }}
                    />
                    {([
                      { value: 'slow' as const, label: '遅い' },
                      { value: 'normal' as const, label: '普通' },
                      { value: 'fast' as const, label: '速い' },
                    ]).map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSpeedChange(option.value)}
                        className={`relative z-10 px-5 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                          speechSpeed === option.value
                            ? 'text-white'
                            : 'text-white/50 hover:text-white/80'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => isSpeaking && speechSynthesisRef.current?.stop()}
                    disabled={!isSpeaking}
                    className={`mt-2 px-4 py-1 text-xs font-medium rounded-md transition-colors ${
                      isSpeaking
                        ? 'text-white bg-white/20 hover:bg-white/30 cursor-pointer'
                        : 'text-white/20 bg-white/5 cursor-not-allowed'
                    }`}
                  >
                  面接官発話スキップ ▶▶
                </button>
              </div>
            )}

            {/* ユーザーカメラ（右上モーダル風オーバーレイ） */}
            <div className="absolute top-4 right-4 w-32 h-24 md:w-40 md:h-32 bg-gray-900 rounded-lg overflow-hidden shadow-lg border-2 border-white z-20">
              <video
                ref={videoRef}
                className={`w-full h-full object-cover ${videoEnabled && videoStream ? 'visible' : 'invisible'}`}
                autoPlay
                muted
                playsInline
                style={{ transform: 'scaleX(-1)' }}
              />
              {(!videoEnabled || !videoStream) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                  {videoEnabled ? (
                    <>
                      <User className="w-6 h-6 mb-1" />
                      <p className="text-xs">起動中...</p>
                    </>
                  ) : (
                    <>
                      <VideoOff className="w-6 h-6 mb-1" />
                      <p className="text-xs">カメラ無効</p>
                    </>
                  )}
                  {/* 復旧手段は「映像が出ていないとき」だけ出す。
                      常設だと候補者には「カメラ再起動」が何を直すのか分からない。 */}
                  <button
                    onClick={() => startCamera()}
                    className="mt-2 px-2 py-1 bg-white/20 hover:bg-white/30 text-white text-[10px] rounded transition-colors"
                  >
                    再接続
                  </button>
                </div>
              )}

              {/* カメラエラー表示 */}
              {cameraError && (
                <div className="absolute inset-0 bg-red-900 bg-opacity-75 flex items-center justify-center p-2">
                  <div className="text-center">
                    <VideoOff className="w-4 h-4 text-red-400 mx-auto mb-1" />
                    <p className="text-red-200 text-xs leading-tight">エラー</p>
                  </div>
                </div>
              )}

              {/* 録画状態表示 */}
              {screenshotState.isCapturing && (
                <div className="absolute top-1 right-1 bg-red-600 text-white text-xs px-1 py-0.5 rounded flex items-center space-x-1 animate-pulse">
                  <Circle className="w-1.5 h-1.5 fill-current" />
                  <span>REC</span>
                </div>
              )}

              {/* ユーザー情報表示 */}
              <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 px-1 py-0.5 rounded text-white text-xs">
                あなた
              </div>
            </div>

            {/* スクリーンショットエラー表示（候補者に見せる必要はないためdev環境のみ表示） */}
            {isDevelopment() && screenshotError && (
              <div className="absolute top-4 left-4 bg-red-900 bg-opacity-90 text-red-200 text-sm p-3 rounded-lg max-w-sm z-20">
                <p className="whitespace-pre-line mb-2">{screenshotError}</p>
                {!screenshotError.includes('再試行します') && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => startScreenshotCapture(0)}
                      className="px-2 py-1 bg-red-700 hover:bg-red-600 text-red-100 rounded text-xs transition-colors"
                    >
                      🔄 再試行
                    </button>
                    <button
                      onClick={() => setScreenshotError('')}
                      className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-100 rounded text-xs transition-colors"
                    >
                      ✕ 閉じる
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 下部固定テロップバー（直近の面接官テキストを常時表示） - 上 */}
          {isTelopVisible && (
            <div className="px-4 py-3 bg-black text-white flex-shrink-0 max-h-[28vh] overflow-y-auto">
              <div className="flex items-start space-x-2">
                <Bot className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  {companyName && (
                    <p className="text-xs text-green-400 mb-1 font-medium">{companyName} 採用担当</p>
                  )}
                  <p className="text-sm leading-relaxed">
                    {currentSpeechText || lastInterviewerText}
                  </p>
                  {currentSpeechText && (
                    <div className="flex items-center mt-2 space-x-1">
                      <div
                        className="w-1.5 h-1.5 bg-green-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0s' }}
                      ></div>
                      <div
                        className="w-1.5 h-1.5 bg-green-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0.1s' }}
                      ></div>
                      <div
                        className="w-1.5 h-1.5 bg-green-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0.2s' }}
                      ></div>
                      <span className="text-green-400 text-xs ml-1">読み上げ中...</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 下部固定：あなたの番です（最下段） */}
          {isUserTurnVisible && (
            <div className="bg-blue-600 bg-opacity-90 text-white p-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Mic className="w-5 h-5 text-blue-200 animate-pulse" />
                  <div className="text-center">
                    <p className="text-lg font-medium">あなたの番です</p>
                    <p className="text-sm text-blue-200 mt-1">お話しください</p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div
                      className="w-2 h-2 bg-blue-300 rounded-full animate-ping"
                      style={{ animationDelay: '0s' }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-blue-300 rounded-full animate-ping"
                      style={{ animationDelay: '0.2s' }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-blue-300 rounded-full animate-ping"
                      style={{ animationDelay: '0.4s' }}
                    ></div>
                  </div>
                </div>
                {/* 回答終了ボタン */}
                <button
                  onClick={async () => {
                    await submitResponse();
                  }}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors"
                >
                  回答を終了する
                </button>
              </div>
            </div>
          )}

          {/* コントロール */}
          {/* 面接開始前だけの操作バー。面接中は表示しない。
              以前は面接中も常設で、カメラ再起動・スクリーンショット制御・面接終了が並んでいたが、
              ・カメラ「再起動」は候補者に意味が伝わらない（映像が出ていないときだけ出す方式に変更）
              ・スクリーンショット制御は監督機能であり候補者が止められるべきではない（非表示化）
              ・「面接終了」は「回答を終了する」と近接して押し間違えやすい（ヘッダーへ移設）
              という理由で、面接中は段ごと無くした。縦の段が減るぶんテロップと映像に余裕が出る。 */}
          {!isInterviewStarted && (
            <div className="p-3 bg-gray-50 flex items-center justify-end flex-shrink-0">
              <button
                onClick={() => setShowInstructionModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm flex items-center space-x-1"
              >
                <Phone className="w-4 h-4" />
                <span>説明を再表示</span>
              </button>
            </div>
          )}
        </div>

        {/* カメラ/マイク権限失効による一時停止画面 */}
        {permissionBlocked && (
          <div className="fixed inset-0 bg-black bg-opacity-85 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
              <div className="bg-red-600 px-6 py-4 flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {permissionBlocked === 'camera' ? (
                    <VideoOff className="w-7 h-7 text-white" />
                  ) : (
                    <MicOff className="w-7 h-7 text-white" />
                  )}
                </div>
                <div>
                  <p className="text-white font-bold text-base leading-tight">
                    {permissionBlocked === 'camera' ? 'カメラ' : 'マイク'}へのアクセスが無効になっています
                  </p>
                  <p className="text-red-100 text-xs mt-0.5">面接を一時停止しています</p>
                </div>
              </div>
              <div className="px-6 py-6 text-center">
                <p className="text-sm text-gray-700 mb-4">
                  ブラウザの設定から{permissionBlocked === 'camera' ? 'カメラ' : 'マイク'}
                  へのアクセスを再度許可してください。許可され次第、自動的に面接を再開します。
                </p>
                <div className="flex items-center justify-center space-x-2 text-gray-400">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-xs">許可を確認しています...</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 面接処理中画面 */}
        {interviewPhase === 'processing' && (
          <div className="fixed inset-0 bg-black bg-opacity-85 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* 警告ヘッダー */}
              <div className="bg-red-600 px-6 py-4 flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-7 h-7 text-white animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-bold text-base leading-tight">ブラウザを閉じないでください</p>
                  <p className="text-red-100 text-xs mt-0.5">データが失われる可能性があります</p>
                </div>
              </div>

              {/* 本文 */}
              <div className="px-6 py-6">
                <div className="text-center mb-5">
                  <div className="relative w-16 h-16 mx-auto mb-4">
                    <div className="absolute inset-0 rounded-full bg-blue-100 animate-ping opacity-50"></div>
                    <div className="relative w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">面接結果を処理中...</h2>
                  <p className="text-xs text-gray-400">この処理には数分かかることがあります</p>
                </div>

                <div className="space-y-3 mb-5">
                  <div className="flex items-center space-x-3 bg-green-50 rounded-lg px-4 py-3">
                    <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse flex-shrink-0"></div>
                    <span className="text-sm text-green-800 font-medium">録画データを保存中</span>
                  </div>
                  <div className="flex items-center space-x-3 bg-blue-50 rounded-lg px-4 py-3">
                    <div className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse flex-shrink-0" style={{ animationDelay: '0.2s' }}></div>
                    <span className="text-sm text-blue-800 font-medium">AI評価を生成中</span>
                  </div>
                  <div className="flex items-center space-x-3 bg-purple-50 rounded-lg px-4 py-3">
                    <div className="w-2.5 h-2.5 bg-purple-500 rounded-full animate-pulse flex-shrink-0" style={{ animationDelay: '0.4s' }}></div>
                    <span className="text-sm text-purple-800 font-medium">面接結果をデータベースに保存中</span>
                  </div>
                </div>

                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 text-center">
                  <p className="text-sm font-bold text-red-700">
                    完了画面が表示されるまで、<br />絶対にこのページを離れないでください。
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI面接官ステータス（簡易表示） */}
        {isInterviewStarted && aiError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-red-700">{aiError}</span>
              <button
                onClick={async () => {
                  setAiError('');
                  const testResult = await aiInterviewService.testConnection();
                  if (!testResult.success) {
                    setAiError(testResult.error || '接続エラー');
                  }
                }}
                className="ml-auto text-xs text-red-600 underline hover:no-underline"
              >
                再試行
              </button>
            </div>
          </div>
        )}

        {/* 面接キャンセル完了画面 */}
        {interviewPhase === 'cancelled' && (
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <PhoneOff className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">面接キャンセル</h2>
              <p className="text-gray-600 mb-4 text-sm">
                面接がキャンセルされました
                <br />
                面接時間: {formatTime(timeElapsed)}
              </p>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-orange-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-orange-900 text-xs">面接時間</h4>
                  <p className="text-lg font-bold text-orange-600">{formatTime(timeElapsed)}</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-orange-900 text-xs">回答数</h4>
                  <p className="text-lg font-bold text-orange-600">
                    {Math.floor(chatHistory.length / 2)}
                  </p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-orange-900 text-xs">ステータス</h4>
                  <p className="text-lg font-bold text-orange-600">キャンセル</p>
                </div>
              </div>

              <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800 text-center">
                  面接がキャンセルされました。
                  <br />
                  会話ログは保存されましたが、評価は行われません。
                  <br />
                  <span className="text-xs text-orange-600 mt-1 block">
                    再面接をご希望の場合は採用担当者にお問い合わせください。
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 面接完了画面 */}
        {interviewPhase === 'completed' && (
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Bot className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">面接完了</h2>
              <p className="text-gray-600 mb-4 text-sm">
                お疲れ様でした！
                <br />
                面接時間: {formatTime(timeElapsed)}
              </p>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-green-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-green-900 text-xs">面接時間</h4>
                  <p className="text-lg font-bold text-green-600">{formatTime(timeElapsed)}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-green-900 text-xs">回答数</h4>
                  <p className="text-lg font-bold text-green-600">
                    {Math.floor(chatHistory.length / 2)}
                  </p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-green-900 text-xs">時間</h4>
                  <p className="text-lg font-bold text-green-600">{formatTime(timeElapsed)}</p>
                </div>
              </div>

              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-sm text-green-800 text-center">
                  面接が正常に完了しました。
                  <br />
                  後日、面接結果についてご連絡いたします。
                  <br />
                  <span className="text-xs text-green-600 mt-1 block">
                    ありがとうございました。
                  </span>
                </p>
              </div>

              {/* 面接終了ボタンは削除（手動終了機能に統合） */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIInterviewSystem;
